import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid,
  CircularProgress
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Helmet } from 'react-helmet-async';
import { blogPostsData } from './blogData';
import BlogCard from './BlogCard';
import { logAnalyticsEvent } from '../../utils/analytics';
import NewsletterSignup from '../common/NewsletterSignup';
import config from '../../config';
import { color, r } from '../../theme/tokens';
import './BlogPost.css';

const EDGE_FN_URL = `${config.EDGE_FUNCTION_URL}/getBlogPosts`;

async function fetchFromEdgeFunction() {
  const res = await fetch(EDGE_FN_URL);
  if (!res.ok) throw new Error(`Edge function returned ${res.status}`);
  const data = await res.json();
  return data.posts || [];
}

async function fetchFromIndexJson() {
  const res = await fetch('/blog/index.json');
  if (!res.ok) throw new Error('index.json not found');
  const data = await res.json();
  return data.posts || [];
}

function BlogPage() {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function loadPosts() {
      // Tier 1: Supabase edge function (when flag is on)
      if (config.FEATURES.USE_SUPABASE_BLOG) {
        try {
          const edgePosts = await fetchFromEdgeFunction();
          if (!cancelled && edgePosts.length > 0) {
            setPosts(edgePosts);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.warn('getBlogPosts edge function failed, falling back:', err.message);
        }
      }

      // Tier 2: static index.json
      try {
        const indexPosts = await fetchFromIndexJson();
        if (!cancelled) {
          setPosts(indexPosts);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn('index.json not found, falling back to blogData.js:', err.message);
      }

      // Tier 3: bundled blogData.js
      if (!cancelled) {
        setPosts(blogPostsData);
        setLoading(false);
      }
    }

    loadPosts();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (posts.length > 0) {
      logAnalyticsEvent('view_blog_page', {
        total_posts: posts.length,
        page_type: 'blog_listing'
      });
    }
  }, [posts]);

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, bgcolor: alpha(color.accent, 0.05) }}>
      <Helmet>
        <title>myStack Blog | Research Insights & Feature Updates</title>
        <meta name="description" content="Explore the latest features, research insights, and updates from the myStack team to optimize your supplement regimen." />
      </Helmet>
      
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Box 
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              position: 'relative',
              pb: 3,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: r(80),
                height: r(3),
                background: color.accent,
                borderRadius: r(2)
              }
            }}
          >
            <Typography 
              component="span" 
              variant="h4"
              sx={{ 
                fontWeight: 700,
                color: color.evergreen,
                letterSpacing: r(0.1),
                display: 'inline-block'
              }}
            >
              myStack
            </Typography>
            <Box component="span" sx={{ width: r(20) }} />
            <Typography 
              component="span"
              variant="h4"
              sx={{ 
                fontWeight: 500,
                color: color.evergreen,
                display: 'inline-block'
              }}
            >
              Blog
            </Typography>
          </Box>
          <Typography 
            variant="h6"
            sx={{
              color: 'text.secondary',
              mx: 'auto',
              mt: 3,
              mb: 6,
              px: { xs: 2, md: 0 },
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              fontWeight: 400,
              maxWidth: r(700),
            }}
          >
            Stay ahead with the latest myStack features, supplement science, and industry developments.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: color.evergreen }} />
          </Box>
        ) : (
          <Grid container spacing={4}>
            {posts.map((post) => (
              <Grid item xs={12} sm={6} md={4} key={post.id || post.slug}>
                <BlogCard post={post} />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
      
      <NewsletterSignup />
    </Box>
  );
}

export default BlogPage;
